import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import {
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';

const EmployeeEngagementModelsConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [engagementModels, setEngagementModels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Unified modal state
  const [engagementModelModal, setEngagementModelModal] = useState({
    open: false,
    isEdit: false,
    formData: { name: '', code: '' },
    originalId: null
  });

  const [deleteModal, setDeleteModal] = useState({ open: false, engagementModel: null });

  const fetchEngagementModels = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getEmployeeEngagementModels();
      setEngagementModels(data);
    } catch (err) {
      showAlert('Failed to load employee engagement models.', 'error', err);
      console.error('Error fetching employee engagement models:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEngagementModels();
  }, []);

  // Unified handler for both add and edit
  const handleSaveEngagementModel = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      if (engagementModelModal.isEdit) {
        // Edit existing engagement model
        if (!engagementModelModal.originalId) {
          showAlert('Invalid employee engagement model data for update', 'error');
          return;
        }

        await organizationApi.updateEmployeeEngagementModel(engagementModelModal.originalId, {
          name: engagementModelModal.formData.name,
          code: engagementModelModal.formData.code
        });
        showAlert('Individual engagement model updated successfully', 'success');
      } else {
        // Add new engagement model
        await organizationApi.createEmployeeEngagementModel({
          name: engagementModelModal.formData.name,
          code: engagementModelModal.formData.code
        });
        showAlert('Individual engagement model added successfully', 'success');
      }

      await fetchEngagementModels();
      // Reset modal state
      setEngagementModelModal({
        open: false,
        isEdit: false,
        formData: { name: '', code: '' },
        originalId: null
      });
    } catch (err) {
      const action = engagementModelModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} individual engagement model.`, 'error', err);
      console.error(`Error ${action}ing employee engagement model:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEngagementModel = (engagementModel) => {
    setDeleteModal({ open: true, engagementModel });
  };

  const confirmDelete = async () => {
    const modelToDelete = deleteModal.engagementModel;
    setDeleteModal({ open: false, engagementModel: null });

    if (!modelToDelete?.id) {
      showAlert('Invalid individual engagement model data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteEmployeeEngagementModel(modelToDelete.id);
      await fetchEngagementModels();
      showAlert('Individual engagement model deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting employee engagement model:', err);
      showAlert(err.message || 'Failed to delete individual engagement model.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEngagementModels = engagementModels.filter(model => {
    if (!searchTerm) return true;
    return model.name.toLowerCase().includes((searchTerm || '').toLowerCase());
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Individual Engagement Models Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Individual Engagement Model"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            <HeaderButton onClick={() => setEngagementModelModal({
              open: true,
              isEdit: false,
              formData: { name: '', code: '' },
              originalId: null
            })}>
              <FaPlus /> Add Individual Engagement Model
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid
                container
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredEngagementModels.map((engagementModel) => (
                  <Grid item key={engagementModel.id}>
                    <Card
                      sx={{
                        width: '100%',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 500,
                            color: '#212529',
                            marginBottom: '4px'
                          }}
                        >
                          {engagementModel.name}
                        </Typography>

                        {engagementModel.code && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#868e96',
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              display: 'block',
                              marginBottom: '8px'
                            }}
                          >
                            Code: {engagementModel.code}
                          </Typography>
                        )}

                        {engagementModel.employeeCount > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#868e96',
                              fontStyle: 'italic'
                            }}
                          >
                            Attached to {engagementModel.employeeCount} consultant{engagementModel.employeeCount !== 1 ? 's' : ''}
                          </Typography>
                        )}

                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => setEngagementModelModal({
                              open: true,
                              isEdit: true,
                              formData: { name: engagementModel.name, code: engagementModel.code || '' },
                              originalId: engagementModel.id
                            })}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDeleteEngagementModel(engagementModel)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing employee engagement models */}
      <Modal
        isOpen={engagementModelModal.open}
        onClose={() => setEngagementModelModal({
          open: false,
          isEdit: false,
          formData: { name: '', code: '' },
          originalId: null
        })}
        onSubmit={handleSaveEngagementModel}
        title={engagementModelModal.isEdit ? "Edit Individual Engagement Model" : "Add New Individual Engagement Model"}
        submitText={engagementModelModal.isEdit ? "Update Individual Engagement Model" : "Add Individual Engagement Model"}
        submitDisabled={!engagementModelModal.formData.name.trim() || !engagementModelModal.formData.code.trim()}
      >
        <Box mb={2}>
          <TextField
            id="employee-engagement-model-name"
            name="name"
            label="Individual Engagement Model Name"
            required
            value={engagementModelModal.formData.name}
            onChange={(e) => setEngagementModelModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter individual engagement model name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="employee-engagement-model-code"
            name="code"
            label="Individual Engagement Model Code"
            required
            value={engagementModelModal.formData.code}
            onChange={(e) => setEngagementModelModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter individual engagement model code (e.g., INT, EXT)"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, engagementModel: null })}
        onConfirm={confirmDelete}
        title="Delete Individual Engagement Model"
        message={`Are you sure you want to delete ${deleteModal.engagementModel?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default EmployeeEngagementModelsConfiguration;
